import { Assert } from '@cotera/utilities';
import { Def } from './erod-schema';
import _ from 'lodash';

const INDENT_WIDTH = 2;

/**
 * Returns a (pretty) string representation of the schema.
 *
 * e.g.
 *
 * ```
 * {
 *   organization: {
 *     id: string | null
 *     name: string | null
 *     website_url: string | null
 *     funding_events: ({
 *       id: string
 *       date: string | null
 *       amount: string | null
 *       currency: string | null
 *     })[] | null
 *     account: {
 *       id: string
 *       domain: string | null
 *       name: string | null
 *       team_id: string | null
 *       organization_id: string | null
 *     }
 *   }
 * }
 * ```
 *
 */
export function prettyPrint<T>(schema: Def<T>): string {
  return prettyPrintImpl(schema, new WeakSet());
}

function prettyPrintImpl<T>(
  schema: Def<T>,
  seen: WeakSet<Def<unknown>>
): string {
  switch (schema.shape.t) {
    case 'base': {
      return prettyPrintBase(schema);
    }

    case 'array':
      return appendNullAndDescription(
        `(${prettyPrintImpl(schema.shape.elementSchema, seen)})[]`,
        schema.isNullable,
        schema.description
      );

    case 'record':
      return appendNullAndDescription(
        `Record<string, ${prettyPrintImpl(
          schema.shape.recordValueSchema,
          seen
        )}>`,
        schema.isNullable,
        schema.description
      );

    case 'discriminatedUnion': {
      const optionsPrinted = schema.shape.options
        .map((opt) => prettyPrintImpl(opt, seen))
        .join(' | ');
      return appendNullAndDescription(
        `discriminatedUnion("${schema.shape.discriminator}", [${optionsPrinted}])`,
        schema.isNullable,
        schema.description
      );
    }

    case 'lazy': {
      if (seen.has(schema)) {
        return appendNullAndDescription(
          'lazy(...)',
          schema.isNullable,
          schema.description
        );
      }
      seen.add(schema);
      try {
        return appendNullAndDescription(
          `lazy(${prettyPrintImpl(schema.shape.get(), seen)})`,
          schema.isNullable,
          schema.description
        );
      } finally {
        seen.delete(schema);
      }
    }

    case 'recordWithKey': {
      return appendNullAndDescription(
        `Record<keySchema, ${prettyPrintImpl(schema.shape.valueSchema, seen)}>`,
        schema.isNullable,
        schema.description
      );
    }

    case 'struct': {
      if (Object.keys(schema.shape.fieldsSchema).length === 0) {
        return appendNullAndDescription(
          '{}',
          schema.isNullable,
          schema.description
        );
      }

      /**
       * Returns string representation of a field within an object schema.
       */
      const printField = (
        fieldName: string,
        fieldSchema: Def<unknown>
      ): string => {
        const indent = ' '.repeat(INDENT_WIDTH);
        const fieldPrinted = prettyPrintImpl(fieldSchema, seen);

        // All but the first line indented (first line is directly appended next to the fieldName)
        const indentedLines = fieldPrinted
          .split('\n')
          .map((line, lineNum) => (lineNum === 0 ? line : indent + line))
          .join('\n');

        return `${indent}${fieldName}: ${indentedLines}`;
      };

      const fieldsPrinted: Record<string, string> = _.mapValues(
        schema.shape.fieldsSchema,
        (fieldSchema, fieldName) => printField(fieldName, fieldSchema)
      );

      const structOutput = `{\n${Object.values(fieldsPrinted).join('\n')}\n}`;

      return appendNullAndDescription(
        structOutput,
        schema.isNullable,
        schema.description
      );
    }

    default:
      return Assert.unreachable(schema.shape);
  }
}

function prettyPrintBase(schema: Def<unknown>): string {
  const ty = schema.ty;

  Assert.assert(schema.shape.t === 'base');
  Assert.assert(
    ty.ty.k === 'primitive' || ty.ty.k === 'range' || ty.ty.k === 'enum'
  );

  const typeLabel = (() => {
    switch (ty.ty.k) {
      case 'primitive':
        return ty.ty.t;

      case 'range': {
        const { start, end } = ty.ty;

        return `range({start: ${start}, end: ${end}})`;
      }

      case 'enum':
        return ty.ty.t.map((variant) => `"${variant}"`).join(' | ');

      default:
        return Assert.unreachable(ty.ty);
    }
  })();

  return appendNullAndDescription(
    typeLabel,
    schema.isNullable,
    schema.description
  );
}

function appendNullAndDescription(
  coreLabel: string,
  isNullable: boolean,
  description: string | null
): string {
  const withNull = isNullable ? `${coreLabel} | null` : coreLabel;

  const withDescription = description
    ? `${withNull} /* ${description} */`
    : withNull;

  return withDescription;
}
